import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { punchcardService } from '../services/punchcardService';
import { punchcardKeys } from './queryKeys';
import { findMatchingRecentCollect } from '../utils/reconcile';
import { PUNCHCARD_RECONCILE_WINDOW_MS, PUNCHCARD_SUBMIT_TIMEOUT_MS } from '../constants';
import type { SubmitPunchcardPayload } from '../types';

/**
 * Submits a punch with a client-side timeout, reconciling against recent collects
 * before surfacing an error. PLATFORM-3336: the backend's only duplicate guard is
 * a uniqueness constraint on the exact `datetime` sent (confirmed against the real
 * backend — resubmitting the identical `datetime` returns 422 "There is already a
 * collection on this date and time"). A retry always generates a fresh `datetime`,
 * so it sails past that constraint — if the timed-out request actually landed
 * server-side, blindly retrying would create a duplicate punch. Exported for tests.
 */
export async function submitWithReconciliation(payload: SubmitPunchcardPayload): Promise<void> {
  try {
    await punchcardService.submitPunchcard(payload, {
      signal: AbortSignal.timeout(PUNCHCARD_SUBMIT_TIMEOUT_MS),
    });
  } catch (error) {
    if (!axios.isCancel(error)) throw error;
    const collects = await punchcardService.getCollects(payload.agreementUuid);
    const alreadyLanded = findMatchingRecentCollect(collects, payload, PUNCHCARD_RECONCILE_WINDOW_MS);
    if (alreadyLanded) return;
    throw error;
  }
}

export function useSubmitPunchcard() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, SubmitPunchcardPayload>({
    mutationFn: submitWithReconciliation,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: punchcardKeys.collects(variables.agreementUuid),
      });
    },
  });
}
